import { MAX_FILE_SIZE_MB } from "../../Data/SD";

/**
 * One plain message per upload rejection, each with a single next step. The
 * same codes are returned by the API (`errorCode`) and produced by browser
 * validation, so both paths show identical wording.
 */
export const UPLOAD_ERROR_MESSAGES = {
  PDF_ENCRYPTED:
    "This form may be encrypted. Please flatten the PDF (print to PDF) and upload it again.",
  PDF_PASSWORD_REQUIRED:
    "This PDF is password-protected. Remove the password, or print it to PDF, and upload it again.",
  PDF_UNREADABLE:
    "We couldn't open this PDF. Please print it to PDF or save a new copy and upload it again.",
  PDF_NO_PAGES: "This PDF has no pages. Please check the file and upload it again.",
  FILE_EMPTY: "This file is empty. Please choose the original file.",
  FILE_TOO_LARGE: `This file is over ${MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`,
  FILE_TYPE_UNSUPPORTED: "Please upload a PDF, PNG, JPG, or GIF file.",
  FILE_EXTENSION_MISMATCH:
    "This file's type doesn't match its name. Please upload the original file.",
  FILE_ALREADY_UPLOADED: "A file with this name is already on this task.",
  UPLOAD_FAILED: "We couldn't upload this file. Please try again.",
} as const;

export type UploadErrorCode = keyof typeof UPLOAD_ERROR_MESSAGES;

/** Advisory only: restricted PDFs still upload. */
export const PDF_SECURITY_RESTRICTIONS_WARNING =
  "This PDF has security restrictions. If eSign has trouble, flatten it (print to PDF) and upload it again.";

/** Advisory only: dynamic XFA forms still upload. */
export const PDF_XFA_FORM_WARNING =
  "This PDF has fillable form content that may not work with eSign. If eSign has trouble, flatten it (print to PDF) and upload it again.";

/** One combined advisory line when a PDF is both restricted and an XFA form. */
export const PDF_RESTRICTED_XFA_FORM_WARNING =
  "This PDF has fillable form content and security restrictions. If eSign has trouble, flatten it (print to PDF) and upload it again.";

/** Older browser validation codes that share an approved message. */
const CODE_ALIASES: Readonly<Record<string, UploadErrorCode>> = {
  EMPTY_FILE: "FILE_EMPTY",
  UNSUPPORTED_FILE_TYPE: "FILE_TYPE_UNSUPPORTED",
  MALFORMED_PDF: "PDF_UNREADABLE",
  PDF_PAGE_LOAD_FAILED: "PDF_UNREADABLE",
  VALIDATION_TIMED_OUT: "PDF_UNREADABLE",
  PASSWORD_PROTECTED: "PDF_PASSWORD_REQUIRED",
  PDF_HAS_NO_PAGES: "PDF_NO_PAGES",
};

export const normalizeUploadErrorCode = (
  code?: string | null,
): UploadErrorCode | undefined => {
  if (!code) return undefined;
  if (Object.prototype.hasOwnProperty.call(UPLOAD_ERROR_MESSAGES, code)) {
    return code as UploadErrorCode;
  }
  return Object.prototype.hasOwnProperty.call(CODE_ALIASES, code)
    ? CODE_ALIASES[code]
    : undefined;
};

const MAX_PASSTHROUGH_LENGTH = 300;

/** Server text is shown only when it reads like a sentence, never markup or JSON. */
const isPlainServerMessage = (message: string): boolean =>
  message.length > 0 &&
  message.length <= MAX_PASSTHROUGH_LENGTH &&
  !/^[<{[]/.test(message) &&
  !/exception|stack trace|\bat [\w.]+\(/i.test(message);

/**
 * Resolves the message to show for an upload failure. Known codes always use
 * the approved wording; otherwise a plain server message (for example a task
 * lock or permission message) is kept, falling back to the generic failure.
 */
export const getUploadErrorMessage = (
  code?: string | null,
  serverMessage?: string | null,
): string => {
  const normalized = normalizeUploadErrorCode(code);
  if (normalized) return UPLOAD_ERROR_MESSAGES[normalized];
  const trimmed = serverMessage?.trim() ?? "";
  return isPlainServerMessage(trimmed) ? trimmed : UPLOAD_ERROR_MESSAGES.UPLOAD_FAILED;
};

export const formatUploadErrorToast = (fileName: string, message: string): string =>
  fileName ? `${fileName}: ${message}` : message;

export interface UploadErrorDetails {
  message: string;
  errorCode?: string;
  taskDisplayId?: string;
  statusCode: number;
}

/**
 * Reads an upload failure response. Accepts ApiResponse JSON
 * (`message`/`errorCode`), legacy `{ message }` bodies, and plain-text bodies.
 */
export const readUploadErrorResponse = async (
  response: Pick<Response, "status" | "text">,
): Promise<UploadErrorDetails> => {
  let body = "";
  try {
    body = await response.text();
  } catch {
    body = "";
  }

  let message: string | undefined;
  let errorCode: string | undefined;
  let taskDisplayId: string | undefined;
  try {
    const parsed = body ? JSON.parse(body) : null;
    if (parsed && typeof parsed === "object") {
      if (typeof parsed.message === "string") message = parsed.message;
      if (typeof parsed.errorCode === "string") errorCode = parsed.errorCode;
      if (typeof parsed.taskDisplayId === "string") taskDisplayId = parsed.taskDisplayId;
    } else if (typeof parsed === "string") {
      message = parsed;
    }
  } catch {
    message = body;
  }

  if (!errorCode && response.status >= 500) errorCode = "UPLOAD_FAILED";

  return {
    message: getUploadErrorMessage(errorCode, message),
    errorCode,
    taskDisplayId,
    statusCode: response.status,
  };
};
